import * as microsoftTeams from '@microsoft/teams-js';

// ─── Teams SDK Service ───

let isInitialized = false;
let isInTeams = false;
let teamsContext: microsoftTeams.app.Context | null = null;

/**
 * Quick pre-check to determine if we might be running in Teams.
 * This avoids the slow Teams SDK timeout when running in a regular browser.
 * 
 * Teams indicators:
 * - Running in an iframe (window.parent !== window)
 * - Has nativeInterface on window (Teams desktop/mobile)
 * - URL contains Teams-specific parameters
 */
function mightBeInTeams(): boolean {
  // Skip if this is an MSAL popup (window.name starts with 'msal.')
  if (window.name?.startsWith('msal.')) {
    return false;
  }

  // Skip if URL contains OAuth response (code= in hash)
  if (window.location.hash.includes('code=') || window.location.hash.includes('error=')) {
    return false;
  }

  // Check if running in an iframe (Teams embeds the app)
  const inIframe = window.parent !== window || window.self !== window.top;
  
  // Check for Teams-specific window properties
  const hasNativeInterface = 'nativeInterface' in window;
  
  // Check for Teams-specific URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const hasTeamsParam = urlParams.has('inTeams') || 
                        urlParams.has('teamSitePath') || 
                        urlParams.has('context');
  
  // Check for Teams user agent (desktop/mobile app)
  const hasTeamsAgent = navigator.userAgent.includes('Teams');

  return inIframe || hasNativeInterface || hasTeamsParam || hasTeamsAgent;
}

/**
 * Initialize the Teams SDK.
 * Must be called before any other Teams operations.
 * 
 * Uses a quick pre-check to avoid the slow SDK timeout when
 * running in a regular browser (not inside Teams).
 */
export async function initializeTeams(): Promise<boolean> {
  if (isInitialized) {
    return isInTeams;
  }

  // Quick pre-check: if clearly not in Teams, skip initialization
  if (!mightBeInTeams()) {
    isInitialized = true;
    isInTeams = false;
    return false;
  }

  try {
    await microsoftTeams.app.initialize();
    isInitialized = true;
    isInTeams = true;

    // Get Teams context
    teamsContext = await microsoftTeams.app.getContext();

    return true;
  } catch {
    isInitialized = true;
    isInTeams = false;
    return false;
  }
}

/**
 * Check if the app is running inside MS Teams
 */
export function isRunningInTeams(): boolean {
  return isInTeams;
}

/**
 * Get the Teams context (user info, theme, etc.)
 */
export function getTeamsContext(): microsoftTeams.app.Context | null {
  return teamsContext;
}

/**
 * Get SSO token from Teams.
 * This token is issued by Teams and can be exchanged for an access token via OBO flow.
 * @returns The Teams SSO token, or null if not in Teams or auth fails
 */
export async function getTeamsSsoToken(): Promise<string | null> {
  if (!isInTeams) {
    return null;
  }

  try {
    const authTokenRequest: microsoftTeams.authentication.AuthTokenRequestParameters = {
      resources: [], // Empty for SSO token
      silent: true,
    };

    const token = await microsoftTeams.authentication.getAuthToken(authTokenRequest);
    return token;
  } catch (error) {
    console.error('Failed to get Teams SSO token:', error);

    // If silent auth fails, we may need interactive auth
    // This can happen if consent is required
    if (isConsentRequired(error)) {
      return await promptForConsent();
    }

    return null;
  }
}

/**
 * Check if the error indicates consent is required
 */
function isConsentRequired(error: unknown): boolean {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: string }).message.toLowerCase();
    return message.includes('consent') || message.includes('interaction_required');
  }
  return false;
}

/**
 * Prompt user for consent in Teams
 */
async function promptForConsent(): Promise<string | null> {
  try {
    // Use Teams authentication popup for consent
    const result = await microsoftTeams.authentication.authenticate({
      url: `${window.location.origin}/auth-start`,
      width: 600,
      height: 535,
    });

    // After consent, try SSO again
    if (result) {
      return await getTeamsSsoToken();
    }
    return null;
  } catch (error) {
    console.error('Teams consent prompt failed:', error);
    return null;
  }
}

/**
 * Notify Teams that authentication is complete (for popup flow)
 */
export function notifyAuthSuccess(result?: string): void {
  microsoftTeams.authentication.notifySuccess(result);
}

/**
 * Notify Teams that authentication failed (for popup flow)
 */
export function notifyAuthFailure(reason?: string): void {
  microsoftTeams.authentication.notifyFailure(reason);
}

/**
 * Register a handler for theme changes in Teams
 */
export function onThemeChange(handler: (theme: string) => void): void {
  if (!isInTeams) return;

  microsoftTeams.app.registerOnThemeChangeHandler((theme) => {
    handler(theme);
  });
}

/**
 * Get current Teams theme
 */
export function getTeamsTheme(): string | undefined {
  return teamsContext?.app.theme;
}
